/**
 * useClientOfferSelections
 *
 * Manages per-unit decision state (accepted/rejected/null), selected period ids,
 * and granularity for the authenticated client offer view.
 *
 * Owns the shared client-offer-review-v2 period axis (buildReviewPeriods) so
 * default-period derivation on accept and granularity re-mapping always agree
 * with the axis ClientOfferReviewShell renders — see
 * ClientOfferReviewShellProps.granularity for how the axis stays in sync
 * across this hook (owner) and the shell (controlled consumer).
 *
 * Traceability: client-offer-view-completion / Task 4.2
 */
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@ooh/ui/components/sonner';
import type { ClientDecision } from '@ooh/ui/components/client-offer-view';
import type { PeriodGranularity } from '@ooh/contracts/period-granularity';
import type { ClientOfferViewUnit, UnitDecisionEntry, CarryForwardPrefill } from '@ooh/contracts';
import type { ClientOfferReviewPeriod } from '../../client-offer-review';
import {
  buildReviewPeriodAxisFromEnrichedUnits,
  buildReviewPeriods,
  getDefaultPeriodIdsFromHalves,
} from '../lib/build-review-periods';
import { mergeSelectedPeriodsToRanges } from '../lib/merge-selected-periods';
import { seedSelectionsFromPrefill } from '../lib/seed-selections-from-prefill';
import type { SeededSelections } from '../lib/seed-selections-from-prefill';
import {
  clearStoredClientOfferSelections,
  readStoredClientOfferSelections,
  writeStoredClientOfferSelections,
  type ClientOfferSelectionsScope,
  type StoredClientOfferSelections,
} from '../lib/client-offer-selections-storage';

/**
 * Scopes the sessionStorage persistence key (client-offer-interactive-view /
 * Client Review Selections Survive Navigation Away From The Offer Page).
 * `scopeId` is the offer id (SSO) or the public token; `versionId` is the
 * offer version currently being reviewed — a stored entry for a different
 * version or a different scopeId is ignored on read.
 */
export interface ClientOfferSelectionsPersistenceParams {
  scope: ClientOfferSelectionsScope;
  scopeId: string;
  versionId: string;
}

interface UseClientOfferSelectionsParams {
  enrichedById: Map<number, ClientOfferViewUnit>;
  acceptedAt: Date | null;
  validUntil: Date;
  carryForwardPrefill: CarryForwardPrefill;
  /** Campaign window the shared period axis is enumerated over — null until configured
   * (see build-review-periods.ts's window handling for the "never configured" fallback). */
  campaignFrom: Date | null;
  campaignTo: Date | null;
  persistence: ClientOfferSelectionsPersistenceParams;
}

interface SelectionState {
  decisions: Record<string, ClientDecision>;
  setDecisions: React.Dispatch<React.SetStateAction<Record<string, ClientDecision>>>;
  selectedPeriods: Record<string, string[]>;
  setSelectedPeriods: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  granularity: PeriodGranularity;
  setGranularity: React.Dispatch<React.SetStateAction<PeriodGranularity>>;
  /** Row ids whose accept state and periods are locked (see SeededSelections). Stable for the
   * lifetime of the hook — the carry-forward pre-fill is computed once, on mount. */
  lockedRowIds: Set<string>;
}

/** Initial display granularity for the client-offer-review-v2 axis — shared by the
 * `granularity` useState below and the axis seedSelectionsFromPrefill seeds against, so
 * the two never drift (offer-decision-carry-forward round-trip bug fix). Not exported —
 * only consumed within this hook. */
const INITIAL_GRANULARITY: PeriodGranularity = 'HALF_MONTH';

/**
 * Lazily reads a matching stored entry exactly once and caches it on the given ref —
 * mirrors the seedRef guarded-lazy-initialization pattern below. Extracted so
 * `useSelectionState` stays under the file's max-lines-per-function budget.
 */
function useStoredSelectionsOnce(
  persistence: ClientOfferSelectionsPersistenceParams,
): StoredClientOfferSelections | null {
  const storedRef = useRef<StoredClientOfferSelections | null | undefined>(undefined);
  if (storedRef.current === undefined) {
    storedRef.current = readStoredClientOfferSelections(
      persistence.scope,
      persistence.scopeId,
      persistence.versionId,
    );
  }
  return storedRef.current;
}

function useSelectionState(
  carryForwardPrefill: CarryForwardPrefill,
  enrichedById: Map<number, ClientOfferViewUnit>,
  campaignFrom: Date | null,
  campaignTo: Date | null,
  language: string,
  persistence: ClientOfferSelectionsPersistenceParams,
): SelectionState {
  // Lazily compute the carry-forward seed exactly once and share it between the two
  // useState initializers below — avoids calling the pure seedSelectionsFromPrefill
  // twice with identical arguments on mount. See React docs "Avoiding recreating the
  // ref contents" for the guarded-ref lazy-initialization pattern this mirrors.
  const seedRef = useRef<SeededSelections | null>(null);
  if (seedRef.current === null) {
    // Seed against the SAME axis this hook renders at mount (INITIAL_GRANULARITY) — a
    // raw AvailabilityPeriodCell id is NOT valid input here, see
    // seedSelectionsFromPrefill's own docstring for the round-trip bug this avoids.
    const { periods: seedPeriods } =
      campaignFrom && campaignTo
        ? buildReviewPeriods({ from: campaignFrom, to: campaignTo }, INITIAL_GRANULARITY, language)
        : { periods: [] };
    seedRef.current = seedSelectionsFromPrefill(carryForwardPrefill, enrichedById, seedPeriods);
  }
  const seed = seedRef.current;

  // A valid stored entry (matching scope/scopeId/versionId) wins over the carry-forward
  // seed for `decisions`/`selectedPeriods`/`granularity` — it represents the client's own
  // in-progress review, which is what navigating to a unit detail screen and back (or a
  // reload) must restore. `lockedRowIds` below still comes from the carry-forward seed
  // regardless: a carried-forward binding unit's lock is server truth, not something a
  // stale/tampered stored entry can escape.
  const stored = useStoredSelectionsOnce(persistence);

  const [decisions, setDecisions] = useState<Record<string, ClientDecision>>(
    () => stored?.decisions ?? seed.decisions,
  );
  const [selectedPeriods, setSelectedPeriods] = useState<Record<string, string[]>>(
    () => stored?.selectedPeriods ?? seed.selectedPeriods,
  );
  const [granularity, setGranularity] = useState<PeriodGranularity>(
    () => stored?.granularity ?? INITIAL_GRANULARITY,
  );

  // Written on every decisions/selectedPeriods/granularity change (including the initial
  // mount, which is a harmless re-write of what was just restored/seeded above).
  useEffect(() => {
    writeStoredClientOfferSelections(
      persistence.scope,
      persistence.scopeId,
      persistence.versionId,
      {
        decisions,
        selectedPeriods,
        granularity,
      },
    );
    // persistence identity is stable per mount (scope/scopeId/versionId don't change
    // without remounting the whole shell), so it's intentionally excluded here to avoid
    // re-running on an object identity change that carries no new data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decisions, selectedPeriods, granularity]);

  return {
    decisions,
    setDecisions,
    selectedPeriods,
    setSelectedPeriods,
    granularity,
    setGranularity,
    lockedRowIds: seed.lockedRowIds,
  };
}

/**
 * Resolves the selectedPeriods entry for a unit being accepted: keep an existing
 * non-empty custom selection (made via handlePeriodSelect BEFORE accepting), or
 * fall back to the unit's full default window when nothing was picked yet.
 * Used by handleDecisionChange (single-unit accept) — CU-86caypxnf review finding:
 * a prior implementation reimplemented the "always overwrite" version of this
 * logic, silently dropping a custom pick made before accepting.
 */
function resolveAcceptedPeriods(
  existing: string[] | undefined,
  defaultPeriods: string[],
): string[] {
  return existing && existing.length > 0 ? existing : defaultPeriods;
}

/* eslint-disable max-lines-per-function -- selection handlers for 4 actions plus axis wiring */
export function useClientOfferSelections({
  enrichedById,
  acceptedAt,
  validUntil,
  carryForwardPrefill,
  campaignFrom,
  campaignTo,
  persistence,
}: UseClientOfferSelectionsParams) {
  const { i18n } = useTranslation('offers');
  const {
    decisions,
    setDecisions,
    selectedPeriods,
    setSelectedPeriods,
    granularity,
    setGranularity,
    lockedRowIds,
  } = useSelectionState(
    carryForwardPrefill,
    enrichedById,
    campaignFrom,
    campaignTo,
    i18n.language,
    persistence,
  );

  const clearPersistedSelections = useCallback(() => {
    clearStoredClientOfferSelections(persistence.scope, persistence.scopeId, persistence.versionId);
  }, [persistence.scope, persistence.scopeId, persistence.versionId]);

  const periodsDisabled = useMemo<boolean>(
    () => acceptedAt != null || validUntil.getTime() < Date.now(),
    [acceptedAt, validUntil],
  );

  const { periods, halvesByUnitId } = useMemo(
    () =>
      buildReviewPeriodAxisFromEnrichedUnits(
        enrichedById,
        granularity,
        campaignFrom,
        campaignTo,
        i18n.language,
      ),
    [enrichedById, granularity, campaignFrom, campaignTo, i18n.language],
  );

  const getDefaultPeriods = useCallback(
    (unitId: string): string[] =>
      getDefaultPeriodIdsFromHalves(periods, halvesByUnitId.get(unitId) ?? []),
    [periods, halvesByUnitId],
  );

  const handleDecisionChange = useCallback(
    (itemId: string, decision: ClientDecision) => {
      // A locked unit's accept state is binding — carried forward from a prior version
      // with unchanged terms AND an active SOLD reservation for this offer — and SHALL
      // NOT be clearable from either client-facing route (offer-decision-carry-forward /
      // Locked carry-forward entries are enforced on finalize). The disabled accept/reject
      // controls are the primary affordance; this
      // guard is defense-in-depth against any other entry point (bulk select/clear-all,
      // granularity re-map) that might otherwise reach this same setter.
      if (lockedRowIds.has(itemId)) return;
      setDecisions((prev) => ({ ...prev, [itemId]: decision }));
      if (decision === 'accepted') {
        // CU-86caypxnf: a client can click free periods to build a custom selection
        // (per the "Kliknij wolny okres, aby wybrać własny termin" hint) BEFORE
        // clicking Akceptuj. Only fall back to the full default window when nothing
        // was already picked — otherwise Akceptuj silently discards the custom pick.
        setSelectedPeriods((prev) => ({
          ...prev,
          [itemId]: resolveAcceptedPeriods(prev[itemId], getDefaultPeriods(itemId)),
        }));
      } else {
        setSelectedPeriods((prev) => ({ ...prev, [itemId]: [] }));
      }
    },
    [getDefaultPeriods, lockedRowIds, setDecisions, setSelectedPeriods],
  );

  const handleGranularityChange = useCallback(
    (g: PeriodGranularity) => {
      // Per spec (client-offer-interactive-view): changing granularity SHALL re-map each
      // accepted unit's selected periods to the new granularity's defaults. Computes the
      // NEW axis synchronously here rather than relying on the `periods`/`halvesByUnitId`
      // memo above (still keyed to the OUTGOING granularity — React state updates are not
      // synchronous, so `setGranularity(g)` below has not re-run that memo yet).
      const { periods: newPeriods, halvesByUnitId: newHalvesByUnitId } =
        buildReviewPeriodAxisFromEnrichedUnits(
          enrichedById,
          g,
          campaignFrom,
          campaignTo,
          i18n.language,
        );
      setGranularity(g);
      setSelectedPeriods((prev) => {
        const next = { ...prev };
        for (const [itemId, decision] of Object.entries(decisions)) {
          // Excludes locked rows — a carried-forward locked unit is seeded as accepted, and
          // its binding periods SHALL NOT be changeable from any entry point, including
          // granularity re-map (offer-decision-carry-forward / Locked carry-forward entries
          // are enforced on finalize).
          if (decision === 'accepted' && !lockedRowIds.has(itemId)) {
            next[itemId] = getDefaultPeriodIdsFromHalves(
              newPeriods,
              newHalvesByUnitId.get(itemId) ?? [],
            );
          }
        }
        return next;
      });
    },
    [
      campaignFrom,
      campaignTo,
      decisions,
      enrichedById,
      i18n.language,
      lockedRowIds,
      setGranularity,
      setSelectedPeriods,
    ],
  );

  const handlePeriodSelect = useCallback(
    (itemId: string, periodId: string, nowSelected: boolean) => {
      // A locked unit's period selection SHALL NOT be changeable — see handleDecisionChange.
      if (lockedRowIds.has(itemId)) return;
      // No raw-cell validity re-check here: the only caller is the unit card's period
      // chip strip (ClientOfferReviewPeriodChips), which already disables every
      // non-free/locked chip via `pickable` and only wires onClick when pickable —
      // this hook trusts that gate rather than re-deriving it from a second, now
      // axis-shaped, source of truth.
      //
      // client-offer-interactive-view / Per-Unit Availability Grid: selecting a free
      // chip on a unit that is not yet accepted accepts that unit with ONLY the
      // clicked period selected (distinct from the accept control's full-window
      // pre-fill). An already-accepted unit's chip click stays a pure narrow/widen —
      // no decision write — so this must run inside the same guards as every other
      // decision mutation (handleDecisionChange), not around them.
      setDecisions((prev) =>
        prev[itemId] === 'accepted' ? prev : { ...prev, [itemId]: 'accepted' },
      );
      setSelectedPeriods((prev) => {
        const current = prev[itemId] ?? [];
        if (decisions[itemId] !== 'accepted') return { ...prev, [itemId]: [periodId] };
        const next = nowSelected ? [...current, periodId] : current.filter((id) => id !== periodId);
        return { ...prev, [itemId]: next };
      });
    },
    [decisions, lockedRowIds, setDecisions, setSelectedPeriods],
  );

  return {
    decisions,
    selectedPeriods,
    granularity,
    periods,
    periodsDisabled,
    handleDecisionChange,
    handleGranularityChange,
    handlePeriodSelect,
    lockedRowIds,
    clearPersistedSelections,
  };
}
/* eslint-enable max-lines-per-function */

/**
 * buildUnitDecisionsPayload
 *
 * Translates the row-id-keyed `decisions`/`selectedPeriods` state produced by
 * useClientOfferSelections into the `mediaUnitId`-keyed `UnitDecisionEntry[]`
 * payload the whole-offer Accept/Reject endpoints expect. Units left
 * undecided (`null`), units whose `mediaUnitId` cannot be resolved, and
 * accepted units that would resolve to zero merged periods (unavailable
 * enrichment data, or every period deselected) are all omitted — an
 * ACCEPTED entry with empty periods fails UnitDecisionEntrySchema's
 * server-side validation and would block the whole submission, so absence
 * of an entry stands in for the undecided state in every one of these cases
 * (client-offer-decision-persistence). For accepted units with periods,
 * selections are merged into contiguous `{from, to}` ranges (against the
 * shared axis `periods`, not raw cells) before submission.
 *
 * Traceability: client-offer-decision-persistence / Task 4.2
 */
export function buildUnitDecisionsPayload(
  decisions: Record<string, ClientDecision>,
  selectedPeriods: Record<string, string[]>,
  mediaUnitIdByRowId: Map<string, number>,
  periods: ClientOfferReviewPeriod[],
): UnitDecisionEntry[] {
  const entries: UnitDecisionEntry[] = [];
  for (const [rowId, decision] of Object.entries(decisions)) {
    if (decision !== 'accepted' && decision !== 'rejected') continue;
    const mediaUnitId = mediaUnitIdByRowId.get(rowId);
    if (mediaUnitId == null) continue;

    if (decision === 'rejected') {
      entries.push({ unitId: mediaUnitId, decision: 'REJECTED' });
      continue;
    }

    const periodIds = selectedPeriods[rowId] ?? [];
    const mergedPeriods = mergeSelectedPeriodsToRanges(periodIds, periods);
    if (mergedPeriods.length === 0) continue;
    entries.push({ unitId: mediaUnitId, decision: 'ACCEPTED', periods: mergedPeriods });
  }
  return entries;
}

/**
 * Counts units the client marked "accepted" whose decision was silently
 * excluded from the built `unitDecisions` payload above. `buildUnitDecisionsPayload`
 * drops an accepted row for any of three distinct reasons -- an unresolved
 * `mediaUnitId`, missing enrichment data for the unit, or zero merged periods
 * (most commonly because the offer's own campaign date range was never
 * configured before publishing, so there is nothing to default-select and
 * nothing for the client to pick from) -- this count does not distinguish
 * between them. An ACCEPTED entry with empty periods would fail server-side
 * schema validation, so buildUnitDecisionsPayload omits it rather than
 * blocking the whole submission -- but that silent drop previously gave the
 * client zero feedback: the confirmation banner and per-unit "accepted" badge
 * both showed success while the decision was never actually persisted
 * server-side. Callers use this count to warn the client explicitly instead
 * of submitting silently.
 *
 * Traceability: CU-86cay88zz.
 */
export function countUndeliverableAcceptedDecisions(
  decisions: Record<string, ClientDecision>,
  unitDecisions: UnitDecisionEntry[],
): number {
  const acceptedRowCount = Object.values(decisions).filter((d) => d === 'accepted').length;
  const acceptedEntryCount = unitDecisions.filter((e) => e.decision === 'ACCEPTED').length;
  return Math.max(0, acceptedRowCount - acceptedEntryCount);
}

/**
 * Pure trigger for the "some accepted units were dropped" warning -- `onWarn`
 * stays injected so this can be unit-tested with no React/i18n context.
 * Prefer the `useWarnOnDroppedDecisions` hook below in application code; this
 * is exported mainly for direct testing of the trigger condition.
 *
 * Traceability: CU-86cay88zz.
 */
export function warnIfAcceptedDecisionsDropped(
  decisions: Record<string, ClientDecision>,
  unitDecisions: UnitDecisionEntry[],
  onWarn: (droppedCount: number) => void,
): void {
  const droppedCount = countUndeliverableAcceptedDecisions(decisions, unitDecisions);
  if (droppedCount > 0) onWarn(droppedCount);
}

/**
 * Single shared hook for the "some accepted units were dropped" warning --
 * used by both the accept AND reject handlers (a unit can be dropped from
 * either payload identically) in BOTH the SSO (`client-offer-page.tsx`) and
 * public-link (`public-offer-page.tsx`) flows, so all four call sites across
 * both page components consume the exact same toast trigger, message, and
 * i18n key instead of each page re-declaring its own wrapper around
 * `warnIfAcceptedDecisionsDropped` (which previously let the two pages
 * silently diverge).
 *
 * Traceability: CU-86cay88zz.
 */
export function useWarnOnDroppedDecisions(): (
  decisions: Record<string, ClientDecision>,
  unitDecisions: UnitDecisionEntry[],
) => void {
  const { t } = useTranslation('offers');
  return useCallback(
    (decisions: Record<string, ClientDecision>, unitDecisions: UnitDecisionEntry[]) => {
      warnIfAcceptedDecisionsDropped(decisions, unitDecisions, (droppedCount) => {
        toast.warning(t('clientOffer.acceptedDecisionsDroppedWarning', { count: droppedCount }));
      });
    },
    [t],
  );
}
